import React from 'react';
import { NavBar } from './NavBar';
import { HERO_METRICS, OFFER_ANCHOR } from './salesData';
import { HERO_IMAGE_URL } from '../utils/headMeta';

/**
 * Hero mobile-first. Prioridad absoluta: el CTA principal entra en el primer
 * pantallazo de un celular. Orden en mobile: promesa → mockup → CTA → métricas
 * (las métricas pueden quedar por debajo del fold, a propósito).
 * El CTA NO abre el checkout: hace smooth-scroll a la sección de oferta.
 */
export function Hero() {
  return <div id="top" className="bg-ivory" data-id="element-110" data-mp="xanpc0x21jfw">
      <NavBar data-id="element-111" data-mp="xbpbb0flzqce" />

      <section aria-labelledby="hero-title" className="relative mx-auto flex min-h-[calc(100svh-3.6rem)] w-full max-w-6xl flex-col px-4 pb-4 pt-3 sm:px-8 lg:grid lg:min-h-0 lg:grid-cols-[1.02fr_0.98fr] lg:grid-rows-[auto_auto_auto] lg:gap-x-10 lg:py-16" data-id="element-112" data-mp="xb5rfe6eervq">
        {/* Promesa */}
        <div className="animate-hero-copy lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-113" data-mp="3d2tm12xxw8bd">
          <p className="font-sans text-[0.56rem] font-bold uppercase tracking-editorial text-gold-deep sm:text-[0.66rem]" data-id="element-114" data-mp="380w0g222h1f9">
            Sistema profesional de IA
          </p>

          <h1 id="hero-title" className="mt-2 font-display text-[1.6rem] uppercase leading-[1.02] text-charcoal sm:text-[2.6rem] lg:text-[3.15rem] lg:leading-[1.02]" data-id="element-115" data-mp="3o6ki7px9iq8t">
            El sistema de IA para nutricionistas que te devuelve{' '}
            <span className="text-gold-deep" data-id="element-116" data-mp="30iawsp3yo4i7">20 horas por semana</span>
          </h1>

          <p className="mt-2.5 max-w-xl font-sans text-[0.8rem] leading-snug text-charcoal/65 sm:text-[0.98rem] sm:leading-relaxed lg:mt-5" data-id="element-117" data-mp="358re4kawka9u">
            8 kits especializados para aplicar IA a consultas, planes, seguimiento, gestión y
            análisis.
          </p>

          <p className="mt-2 font-sans text-[0.66rem] font-bold uppercase tracking-[0.1em] text-charcoal sm:text-[0.8rem] lg:mt-4" data-id="element-118" data-mp="2vj7wfhyxemgk">
            Sin programar. Sin conocimientos técnicos.
          </p>
        </div>

        {/* Mockup protagonista, flotando suavemente. Más compacto en mobile. */}
        <div className="animate-hero-media relative my-2 flex min-h-[110px] flex-1 items-center justify-center sm:my-3 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:my-0 lg:min-h-0 lg:flex-none lg:self-center" data-id="element-119" data-mp="s3cloaja7jqk">
          <div className="mockup-ground absolute bottom-1 left-1/2 h-6 w-[66%] -translate-x-1/2 blur-md sm:h-12" aria-hidden data-id="element-120" data-mp="i9vr34bbm0lu" />
          <img src={HERO_IMAGE_URL} alt="Colección del Sistema IA para Nutricionistas: los 8 tomos, tablet, teléfono y plantillas impresas." width={1256} height={1256} fetchPriority="high" decoding="async" className="animate-breathe relative h-full max-h-[34vh] w-full object-contain drop-shadow-[0_20px_34px_rgba(34,34,34,0.16)] sm:max-h-[42vh] lg:max-h-[560px]" data-id="element-121" data-mp="1azkufobi6cqm" />
        </div>

        {/* CTA principal: scroll interno a la oferta, nunca al checkout */}
        <div className="lg:col-start-1 lg:row-start-2" data-id="element-122" data-mp="1x9b590wn91ck">
          <a href={OFFER_ANCHOR} className="group flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-full bg-charcoal px-6 py-3.5 text-center font-sans text-[0.84rem] font-bold uppercase tracking-[0.08em] text-ivory shadow-[0_16px_32px_-14px_rgba(34,34,34,0.55)] transition-[transform,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:bg-charcoal-soft focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 sm:text-[0.92rem] lg:mt-8 lg:w-auto lg:px-9" data-id="element-123" data-mp="1xavxukzeea7s">
            Quiero recuperar mis horas
            <span className="transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden data-id="element-124" data-mp="2p1jy7ut8zbke">
              →
            </span>
          </a>
        </div>

        {/* Métricas: pueden quedar por debajo del fold */}
        <div className="lg:col-start-1 lg:row-start-3 lg:self-start" data-id="element-125" data-mp="zlaoxxavdylc">
          <dl className="mt-4 grid grid-cols-4 gap-x-2 border-t border-warm pt-3 sm:gap-x-4 lg:mt-7 lg:border-y lg:pb-5" data-id="element-126" data-mp="znn9mi80in23">
            {HERO_METRICS.map(metric => <div key={metric.label} className="text-center lg:text-left" data-id="element-127" data-mp="3vgvxcsqubh69">
                <dt className="sr-only" data-id="element-128" data-mp="e9tr1roirz2c">{metric.label}</dt>
                <dd data-id="element-129" data-mp="e77ipptt1fj2">
                  <span className="block font-display text-[1.1rem] leading-none text-charcoal sm:text-[1.7rem] lg:text-[2.1rem]" data-id="element-130" data-mp="9aqme8cgbuuw">
                    {metric.value}
                  </span>
                  <span className="mt-1 block font-sans text-[0.48rem] font-semibold uppercase leading-tight tracking-[0.08em] text-charcoal/50 sm:text-[0.6rem]" data-id="element-131" data-mp="3crfs5s47n0ji">
                    {metric.label}
                  </span>
                </dd>
              </div>)}
          </dl>
        </div>
      </section>
    </div>;
}